import { memo, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderCardWrapper } from '../../../FormBuilderCardWrapper';
import { FormBuilderElement } from '../../../FormBuilderElement';
import {
  useFormBuilderFields,
  useFormBuilderFieldWithError,
} from '../../../hooks';
import {
  EFormBuilderFieldType,
  TFormBuilderMultilineInputField,
} from '@/types';

type TProps = {
  field: TFormBuilderMultilineInputField;
  index: number;
};

export const FormBuilderMultilineInput =
  /**
   * Multiline text input field component for form builder with editable label and placeholder
   * @param {TProps} props - Component props
   * @param {TFormBuilderMultilineInputField} props.field - Multiline input field data
   * @param {number} props.index - Field index in the form
   * @returns {JSX.Element} Form builder multiline input field component
   */
  memo(({ field, index }: TProps) => {
    const { t } = useTranslation();
    const inputId = useId();
    const [active, setActive] = useState(false);
    const onActiveChange = useCallback(
      (value: boolean) => setActive(value),
      [],
    );

    const error =
      useFormBuilderFieldWithError<TFormBuilderMultilineInputField>(index);

    const { changeFields, removeField, copyField, moveDownField, moveUpField } =
      useFormBuilderFields(field);

    const changeMultilineInputFields = useCallback(
      (updated: Partial<TFormBuilderMultilineInputField>) => {
        changeFields({ ...field, ...updated });
      },
      [changeFields, field],
    );
    return (
      <FormBuilderCardWrapper
        variant={EFormBuilderFieldType.MULTILINE_INPUT}
        error={!!error}
        active={active}
        onActiveChange={onActiveChange}
        onRemove={removeField}
        onDownMove={moveDownField}
        onUpMove={moveUpField}
        onDuplicate={copyField}
      >
        <FormBuilderElement
          label={
            <FormBuilderElement.Label
              label={field.label}
              htmlFor={`${inputId}-input`}
              onLabelChange={(label) => changeMultilineInputFields({ label })}
              placeholder={t('form-builder.your-label-here')}
              required={field.required}
              error={!!error?.label}
            />
          }
          required={field.required}
          onRequiredChange={(required) =>
            changeMultilineInputFields({ required })
          }
        >
          <FormBuilderElement.MultiLineInput
            id={inputId}
            placeholder={t('form-builder.placeholder-text')}
            value={field.placeholder}
            onChange={(e) =>
              changeMultilineInputFields({ placeholder: e.target.value })
            }
          />
        </FormBuilderElement>
      </FormBuilderCardWrapper>
    );
  });

FormBuilderMultilineInput.displayName = 'FormBuilderMultilineInput';
